import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import FixedCostsBreakdown from "@/components/FixedCostsBreakdown";
import {
  TrendingUp,
  DollarSign,
  Activity,
  Users,
  Target,
  Settings,
  PlayCircle,
} from "lucide-react";

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const COST_LABELS = {
  infrastructure: "Infrastructure",
  llm: "LLM (Claude / OpenAI)",
  email_delivery: "Email delivery",
  sms_delivery: "SMS delivery",
  demo_video_render: "Demo video render",
  stripe_processing: "Stripe processing",
  support: "Customer success",
  vendor_payouts_handling: "Vendor payouts handling",
  engineering_allocation: "Engineering allocation",
  sales_marketing: "Sales & marketing (CAC)",
};

export default function AdminPlatformEconomics() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);

  const [proj, setProj] = useState({
    subscribers: { connect: 50, grow: 25, scale: 5 },
    monthly_growth_pct: 8,
    monthly_churn_pct: 3.5,
    months: 12,
  });
  const [projection, setProjection] = useState(null);
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/economics/summary");
      setSummary(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveTierCosts = async (tierId, costs) => {
    try {
      await api.patch(`/admin/economics/costs/${tierId}`, { costs });
      toast.success(`${tierId} cost lines updated.`);
      setEditingTier(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runProjection = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/economics/projection", proj);
      setProjection(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Business"
      title="Platform Economics"
      intro="Per-tier unit economics, live margin tracking, fixed-cost configuration, and a 12–24 month financial projection model."
    >
      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && summary && (
        <div className="space-y-6" data-testid="admin-economics">
          {/* KPI strip */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="economics-kpis">
            <Kpi label="Live MRR" value={usd(summary.totals.mrr)} icon={DollarSign} accent="bg-emerald-500" />
            <Kpi label="Live ARR" value={usd(summary.totals.arr)} icon={TrendingUp} accent="bg-blue-500" />
            <Kpi label="Variable cost" value={usd(summary.totals.variable_cost)} icon={Activity} accent="bg-amber-500" />
            <Kpi
              label="Net monthly"
              value={usd(summary.totals.net_monthly)}
              icon={Target}
              accent={summary.totals.net_monthly >= 0 ? "bg-emerald-500" : "bg-rose-500"}
            />
          </div>

          {/* Per-tier unit economics */}
          <section data-testid="economics-tier-table">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold text-slate-900">Unit economics per tier</h3>
              <span className="text-xs text-slate-500">Fixed costs covered when contribution ≥ {usd(summary.totals.fixed_costs)}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Tier</th>
                    <th className="text-right px-4 py-3">Price</th>
                    <th className="text-right px-4 py-3">Cost</th>
                    <th className="text-right px-4 py-3">Margin</th>
                    <th className="text-right px-4 py-3">Margin %</th>
                    <th className="text-right px-4 py-3">Live subs</th>
                    <th className="text-right px-4 py-3">Live MRR</th>
                    <th className="text-right px-4 py-3">Break-even (this tier only)</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {summary.tiers.map((t) => (
                    <tr key={t.id} data-testid={`economics-tier-row-${t.id}`}>
                      <td className="px-4 py-3 font-bold text-slate-900">{t.name}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{usd(t.price_monthly)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-rose-700">{usd(t.cost_total)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-semibold">
                        {usd(t.gross_margin_usd)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{t.gross_margin_pct}%</td>
                      <td className="px-4 py-3 text-right tabular-nums">{t.live_subscribers}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{usd(t.live_mrr)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {summary.breakeven_subscribers_per_tier_only?.[t.id] ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setEditingTier(t)}
                          data-testid={`economics-edit-costs-${t.id}`}
                          className="text-xs font-semibold rounded-full px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"
                        >
                          <Settings className="w-3 h-3" /> Edit costs
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Fixed costs config — detailed breakdown + variables tool */}

          {/* Projection model */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="economics-projection">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-display text-lg font-bold text-slate-900">Revenue projection model</h3>
              <button
                type="button"
                onClick={runProjection}
                disabled={running}
                data-testid="economics-run-projection"
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
              >
                <PlayCircle className="w-3.5 h-3.5" />
                {running ? "Running…" : "Run projection"}
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.tiers.map((t) => (
                <label key={t.id} className="text-xs">
                  <span className="block font-semibold text-slate-700 mb-1">{t.name} starting subscribers</span>
                  <input
                    type="number"
                    min={0}
                    value={proj.subscribers[t.id] ?? 0}
                    onChange={(e) =>
                      setProj((p) => ({
                        ...p,
                        subscribers: { ...p.subscribers, [t.id]: parseInt(e.target.value) || 0 },
                      }))
                    }
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    data-testid={`projection-subs-${t.id}`}
                  />
                </label>
              ))}
              <label className="text-xs">
                <span className="block font-semibold text-slate-700 mb-1">Monthly growth %</span>
                <input
                  type="number"
                  step="0.1"
                  value={proj.monthly_growth_pct}
                  onChange={(e) => setProj({ ...proj, monthly_growth_pct: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  data-testid="projection-growth"
                />
              </label>
              <label className="text-xs">
                <span className="block font-semibold text-slate-700 mb-1">Monthly churn %</span>
                <input
                  type="number"
                  step="0.1"
                  value={proj.monthly_churn_pct}
                  onChange={(e) => setProj({ ...proj, monthly_churn_pct: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  data-testid="projection-churn"
                />
              </label>
              <label className="text-xs">
                <span className="block font-semibold text-slate-700 mb-1">Months</span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={proj.months}
                  onChange={(e) => setProj({ ...proj, months: parseInt(e.target.value) || 12 })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  data-testid="projection-months"
                />
              </label>
            </div>

            {projection && (
              <div className="mt-5 space-y-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="projection-summary">
                  <Kpi label="Ending MRR" value={usd(projection.summary.ending_mrr)} icon={DollarSign} accent="bg-emerald-500" />
                  <Kpi label="Ending ARR" value={usd(projection.summary.ending_arr)} icon={TrendingUp} accent="bg-blue-500" />
                  <Kpi label="Cumulative net" value={usd(projection.summary.cumulative_net)} icon={Activity} accent={projection.summary.cumulative_net >= 0 ? "bg-emerald-500" : "bg-rose-500"} />
                  <Kpi
                    label="Break-even month"
                    value={projection.summary.first_breakeven_month ?? "Not in window"}
                    icon={Target}
                    accent="bg-violet-500"
                  />
                </div>
                <ProjectionChart timeline={projection.timeline} />
                <ProjectionTable timeline={projection.timeline} />
              </div>
            )}
          </section>
        </div>
      )}

      {/* Fixed costs config — kept outside the loading/summary gate so it stays mounted */}
      <div className="mt-6">
        <FixedCostsBreakdown onTotalChange={refresh} />
      </div>

      {editingTier && (
        <CostEditor
          tier={editingTier}
          onClose={() => setEditingTier(null)}
          onSave={(costs) => saveTierCosts(editingTier.id, costs)}
        />
      )}
    </ToolPageShell>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <span className={`w-1 h-10 rounded-full ${accent}`} />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-xl font-bold text-slate-900">{value}</div>
      </div>
      <Icon className="w-4 h-4 text-slate-400" />
    </div>
  );
}

function ProjectionChart({ timeline }) {
  const maxRev = Math.max(...timeline.map((m) => m.mrr), 1);
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
        MRR vs total cost — month over month
      </div>
      <div className="space-y-1.5">
        {timeline.map((m) => {
          const total = m.variable_cost + m.fixed_costs;
          const revPct = (m.mrr / maxRev) * 100;
          const costPct = (total / maxRev) * 100;
          return (
            <div key={m.month} className="flex items-center gap-2 text-[11px]">
              <div className="w-10 text-slate-500 font-semibold">M{m.month}</div>
              <div className="flex-1 relative h-5 bg-white rounded-md overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-emerald-400/80" style={{ width: `${revPct}%` }} />
                <div
                  className="absolute inset-y-0 left-0 border-r-2 border-rose-500"
                  style={{ width: `${Math.min(costPct, 100)}%` }}
                />
              </div>
              <div className="w-20 text-right tabular-nums font-semibold text-slate-900">{usd(m.mrr)}</div>
              <div className={`w-20 text-right tabular-nums ${m.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {usd(m.net)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-slate-500 mt-3">
        Green bar = MRR · Red line = total cost (variable + fixed) · right column = net
      </div>
    </div>
  );
}

function ProjectionTable({ timeline }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-3 py-2">Month</th>
            <th className="text-right px-3 py-2">MRR</th>
            <th className="text-right px-3 py-2">Variable cost</th>
            <th className="text-right px-3 py-2">Fixed cost</th>
            <th className="text-right px-3 py-2">Net</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {timeline.map((m) => (
            <tr key={m.month}>
              <td className="px-3 py-2 font-semibold">M{m.month}</td>
              <td className="px-3 py-2 text-right tabular-nums">{usd(m.mrr)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-rose-700">{usd(m.variable_cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-rose-700">{usd(m.fixed_costs)}</td>
              <td className={`px-3 py-2 text-right tabular-nums font-semibold ${m.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {usd(m.net)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostEditor({ tier, onClose, onSave }) {
  const [costs, setCosts] = useState({ ...tier.costs });
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="economics-cost-editor">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="font-display font-bold text-slate-900">Edit cost lines — {tier.name}</div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            ✕
          </button>
        </div>
        <div className="p-5 space-y-2">
          {Object.entries(costs).map(([k, v]) => (
            <label key={k} className="flex items-center gap-3 text-sm">
              <span className="flex-1 text-slate-700">{COST_LABELS[k] || k}</span>
              <input
                type="number"
                step="0.01"
                value={v}
                onChange={(e) => setCosts({ ...costs, [k]: parseFloat(e.target.value) || 0 })}
                className="w-28 rounded-xl border border-slate-200 px-2 py-1 text-right tabular-nums"
                data-testid={`cost-line-${k}`}
              />
            </label>
          ))}
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(costs)}
            data-testid="cost-editor-save"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800"
          >
            Save costs
          </button>
        </div>
      </div>
    </div>
  );
}
